import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PublicReviewItem } from '../components/ClickableReviewsSummary';
import type { Review } from '../models/CreateReviewRequest';
import type { OrganizationPublicSummary } from '../models/OrganizationPublicSummary';
import type { SpaceSummary } from '../models/Space';
import type { TutorSearchResult } from '../models/Tutor';
import { apiClient } from '../publicApiClient';
import { tutorApiClient } from '../tutorApiClient';

export type OrgTutorsPageResolution = { organizationId: string } | null;

export interface OrgSpacePageData {
  space: SpaceSummary;
  reviewStats: { averageRating: number; reviewCount: number } | null;
  reviews: Review[];
  reviewsLoading: boolean;
}

function reviewStatsFromReviews(reviews: Review[]): { averageRating: number; reviewCount: number } | null {
  if (reviews.length === 0) return null;
  const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
  return {
    averageRating: totalRating / reviews.length,
    reviewCount: reviews.length,
  };
}

async function loadTutorsForOrganization(
  organizationId: string,
  onError: (message: string) => void,
): Promise<TutorSearchResult[]> {
  try {
    return await tutorApiClient.getPublicTutorsByOrganizationId(organizationId);
  } catch (_e) {
    onError("Couldn't load tutors for this organization.");
    return [];
  }
}

async function loadSpacePageData(spaceId: string): Promise<OrgSpacePageData | null> {
  try {
    const space = await apiClient.getSpaceSummary(spaceId);
    let reviews: Review[] = [];
    try {
      reviews = await apiClient.getReviews(spaceId);
    } catch (_e) {
      reviews = [];
    }
    return {
      space,
      reviewStats: reviewStatsFromReviews(reviews),
      reviews,
      reviewsLoading: false,
    };
  } catch (_e) {
    return null;
  }
}

export function useOrgTutorsPageData(resolution: OrgTutorsPageResolution) {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<OrganizationPublicSummary | null>(null);
  const [spaces, setSpaces] = useState<OrgSpacePageData[]>([]);
  const [tutorReviewsById, setTutorReviewsById] = useState<Record<string, PublicReviewItem[]>>({});
  const [tutorReviewsLoadingById, setTutorReviewsLoadingById] = useState<Record<string, boolean>>({});
  const [tutors, setTutors] = useState<TutorSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [tutorError, setTutorError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!resolution) {
        setLoading(false);
        setOrganizationError(t('orgPage.loadError', "Couldn't load organization details."));
        return;
      }

      setLoading(true);
      setOrganizationError(null);
      setTutorError(null);

      try {
        const summary = await apiClient.getOrganizationPublicSummary(resolution.organizationId);
        if (cancelled) return;
        setOrganization(summary);

        const spaceResults = await Promise.all(
          (summary.spaces ?? []).map((space) => loadSpacePageData(space.id)),
        );
        if (cancelled) return;
        setSpaces(spaceResults.filter((item): item is OrgSpacePageData => item != null));

        const roster = await loadTutorsForOrganization(summary.id, (message) => {
          if (!cancelled) {
            setTutorError(t('orgPage.tutorsLoadError', message));
          }
        });
        if (!cancelled) setTutors(roster);
      } catch (_e) {
        if (!cancelled) {
          setOrganization(null);
          setSpaces([]);
          setOrganizationError(t('orgPage.loadError', "Couldn't load organization details."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution?.organizationId]);

  const loadTutorReviews = async (tutorId: string) => {
    if (tutorReviewsById[tutorId] || tutorReviewsLoadingById[tutorId]) return;

    setTutorReviewsLoadingById((prev) => ({ ...prev, [tutorId]: true }));
    try {
      const reviews = await tutorApiClient.getTutorReviews(tutorId);
      setTutorReviewsById((prev) => ({
        ...prev,
        [tutorId]: reviews.map((review) => ({
          id: review.id,
          rating: review.rating,
          comment: review.comment,
          createdAt: review.createdAt,
          username: review.username,
        })),
      }));
    } catch (_e) {
      setTutorReviewsById((prev) => ({ ...prev, [tutorId]: [] }));
    } finally {
      setTutorReviewsLoadingById((prev) => ({ ...prev, [tutorId]: false }));
    }
  };

  return {
    organization,
    spaces,
    tutors,
    loading,
    organizationError,
    tutorError,
    tutorReviewsById,
    tutorReviewsLoadingById,
    loadTutorReviews,
  };
}
